import { push as pushAction } from 'connected-react-router'
import { Button, ComponentVersions_3_0, ComponentVersionsInherit } from 'opensolar-ui'
import React, { useMemo } from 'react'
import { formatTariffValidityPeriod } from 'projectSections/sections/energy/ElectricityUtility/tariffUtils'
import {
  Create,
  Datagrid,
  DeleteButton,
  Edit,
  FilterComp,
  FunctionField,
  SaveButton,
  showNotification as showNotificationAction,
  SimpleForm,
  TextField,
  Toolbar,
  useTranslate,
  withTranslate,
} from 'react-admin'
import { useDateFormatter } from 'util/date'
import { clone, getRoleFromState } from 'util/misc'
import {
  DEFAULT_TARIFF_DATA,
  ExtraFieldByDistributionRule,
  isCalifornianNEM3Utility,
  isReadOnly,
  SECTOR_RESIDENTIAL,
  UTILITY_TARIFF_BILL_FREQUENCY_CHOICES,
} from './common'

import { UtilityTariffCatalogueSourceIcon } from './catalogue/UtilityTariffCatalogueSourceIcon'
import { WHOLESALE_MARGINAL_PRICE_LIMIT_KEYS, WHOLESALE_SPOT_SERIES_CALENDAR } from './wholesaleTariffTypes'

import PropTypes from 'prop-types'
import { connect, useSelector } from 'react-redux'

import AlertError from '@material-ui/icons/ErrorOutlineOutlined'

import { Link } from 'react-router-dom'

import { duplicate as duplicateAction } from '../../actions/restActions'

import compose from 'recompose/compose'

import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import { permissionsSelectors } from 'ducks/permissions'
import { UtilityTariffCatalogList } from './catalogue/UtilityTariffCatalogList'
import { parseSchedules } from 'elements/scheduleDialog/utils'
import PaginationWithActions from '../../elements/PaginationWithActions'
import TariffInputs from './TariffInputs'
import { getUtilityTariffCatalogFilterFieldElements } from './catalogue/utilityTariffCatalogFilterFields'
import { UtilityTariffCreateBootstrap } from './UtilityTariffCreateBootstrap'
import UtilityTariffListEditOrImportButton from './UtilityTariffListEditOrImportButton'
import { UTILITY_TARIFF_GROUPS_LIST_PATH } from './routing/constants'
import { parseUtilityTariffGroupIdFromApiUrl, utilityTariffVersionEditPathForRecord } from './routing/helpers'
import { utilityTariffVersionEditPath } from './routing/paths'

const csvFloatsToArray = (csvFloats) => {
  if (!csvFloats) {
    return []
  } else if (typeof csvFloats === 'string') {
    return csvFloats.split(',').map((v) => parseFloat(v.trim()))
  } else {
    return csvFloats
  }
}

function wholesaleMarginalPriceLimitsForSubmit(raw) {
  const out = {}
  if (!raw || typeof raw !== 'object') {
    return out
  }
  for (const k of WHOLESALE_MARGINAL_PRICE_LIMIT_KEYS) {
    const v = raw[k]
    if (v === undefined || v === '' || v === null) {
      continue
    }
    const n = Number(v)
    if (Number.isFinite(n)) {
      out[k] = n
    }
  }
  return out
}

/** Clears distributor when wholesale; copies or strips wholesale_* keys onto tariffData. */
function applyWholesaleUtilityTariffSubmitFields(values, tariffData) {
  const isWholesaleUtilityTariff = values.tariffData?.wholesale === true || values.tariffData?.wholesale === 'true'

  if (isWholesaleUtilityTariff) {
    delete values.distributor
  }

  if (values.tariffData?.wholesale) {
    tariffData.wholesale = true
    tariffData.wholesale_year = Number(values.tariffData.wholesale_year)
    tariffData.wholesale_country_iso2 = String(values.tariffData.wholesale_country_iso2 || 'AU')
      .trim()
      .toUpperCase()

    const gids = values.tariffData.wholesale_network_tariff_group_ids
    if (Array.isArray(gids) && gids.length > 0) {
      const ids = gids.map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n) && Number.isInteger(n) && n > 0)
      if (ids.length > 0) {
        tariffData.wholesale_network_tariff_group_ids = ids
      } else {
        delete tariffData.wholesale_network_tariff_group_ids
      }
    } else {
      delete tariffData.wholesale_network_tariff_group_ids
    }

    const limits = wholesaleMarginalPriceLimitsForSubmit(values.tariffData?.wholesale_marginal_price_limits)
    if (Object.keys(limits).length > 0) {
      tariffData.wholesale_marginal_price_limits = limits
    } else {
      delete tariffData.wholesale_marginal_price_limits
    }

    const rawSeries = values.tariffData?.wholesale_spot_series
    const series =
      rawSeries != null && String(rawSeries).trim() !== '' ? String(rawSeries).trim() : WHOLESALE_SPOT_SERIES_CALENDAR
    if (series.toLowerCase() === WHOLESALE_SPOT_SERIES_CALENDAR) {
      delete tariffData.wholesale_spot_series
    } else {
      tariffData.wholesale_spot_series = series
    }
  } else {
    delete tariffData.wholesale
    delete tariffData.wholesale_year
    delete tariffData.wholesale_country_iso2
    delete tariffData.wholesale_network_tariff_group_ids
    delete tariffData.wholesale_marginal_price_limits
    delete tariffData.wholesale_spot_series
  }
}

export const UtilityTariffFilter = (props) => (
  <FilterComp {...props}>{getUtilityTariffCatalogFilterFieldElements()}</FilterComp>
)

export const UtilityTariffList = connect(
  (state) => {
    return {
      org_id: state.auth ? state.auth.org_id : null,
      is_staff: state.auth.is_staff,
    }
  },
  { duplicate: duplicateAction }
)(({ accessRights, versionEditLinks, ...props }) => {
  const { allowCreate = true, allowEdit = true, allowDelete = true } = accessRights || {}
  const isAdmin = useSelector((state) => Boolean(getRoleFromState(state)?.is_admin))
  const translate = useTranslate()
  const formatDate = useDateFormatter({ variant: 'date' })
  const isAllRateRowsList = props.resource === 'utility_tariffs' && versionEditLinks === true
  const filterDefaultValues = useMemo(() => {
    if (isAllRateRowsList) {
      return { ...(props.filterDefaultValues || {}), tariff_dataset: 'all' }
    }
    return props.filterDefaultValues
  }, [isAllRateRowsList, props.filterDefaultValues])
  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <UtilityTariffCatalogList
        {...props}
        heading={props.hideBreadCrumbs ? false : 'Utility Tariffs Database'}
        catalogFilter={<UtilityTariffFilter is_staff={props.is_staff} />}
        catalogToolbar={{
          showCreateButton: allowCreate,
          searchBarPlaceholder: props.searchBarPlaceholder,
        }}
        pagination={
          props.is_staff ? (
            <PaginationWithActions
              uploadModel={'utility_tariffs'}
              downloadUrl={
                window.API_BASE_URL + 'orgs/' + props.org_id + '/utility_tariffs/?fieldset=csv&format=csv&ordering=-id'
              }
              downloadFilename="OpenSolarUtilityTariffs"
            />
          ) : undefined
        }
        hasSearch={true}
        perPage={20}
        filterDefaultValues={filterDefaultValues}
      >
        <Datagrid
          rowClick={
            versionEditLinks
              ? (_id, _basePath, record) => utilityTariffVersionEditPathForRecord(record) || false
              : undefined
          }
        >
          <FunctionField
            label=""
            sortable={false}
            headerStyle={{ width: '32px' }}
            cellStyle={{ width: '32px' }}
            render={(record) => (
              <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center' }}>
                <UtilityTariffCatalogueSourceIcon record={record} />
              </span>
            )}
          />
          <TextField source="utility_name" label={'Utility'} emptyText="—" />
          <TextField source="distributor_name" label={'Distributor'} emptyText="—" />
          <TextField source="code" label={'Code'} sortBy="utility_tariff_group__code" emptyText="—" />
          <TextField source="name" label={'Name'} />
          <FunctionField
            label={translate('Validity period')}
            source="valid_from"
            sortBy="valid_from"
            headerStyle={{ width: '160px' }}
            cellStyle={{ width: '160px' }}
            render={(record) => formatTariffValidityPeriod(record, formatDate, translate)}
          />
          <UtilityTariffListEditOrImportButton
            sortable={false}
            source="actions"
            org_id={props.org_id}
            resource="utility_tariffs"
            duplicate={props.duplicate}
            isAdmin={isAdmin}
            allowEdit={allowEdit}
            allowDelete={allowDelete}
            versionEditLinks={versionEditLinks === true}
            headerStyle={{ width: '200px' }}
            cellStyle={{ width: '200px' }}
          />
        </Datagrid>
      </UtilityTariffCatalogList>
    </ComponentVersionsInherit>
  )
})

export { redirectToTariffVersionEdit } from './routing/helpers'

export const UtilityTariffCreate = ({ embeddedUtilityTariffGroupUrl, ...props }) => {
  const user = useSelector(authSelectors.getCurrentUser)
  const { allowCreate } = useSelector((state) => permissionsSelectors.getPermissionByKey(state, 'utility_tariffs'))

  const orgCountry = useSelector(orgSelectors.getOrg)?.country
  const defaultCountry = useMemo(() => {
    return { url: `${window.API_ROOT}/api/countries/${orgCountry?.id}/`, iso2: orgCountry?.iso2 }
  }, [orgCountry])

  const catalogueGroupUrl = useMemo(() => {
    const url =
      embeddedUtilityTariffGroupUrl != null && String(embeddedUtilityTariffGroupUrl).trim() !== ''
        ? String(embeddedUtilityTariffGroupUrl).trim()
        : null
    if (url == null || parseUtilityTariffGroupIdFromApiUrl(url) == null) {
      return null
    }
    return url
  }, [embeddedUtilityTariffGroupUrl])

  const redirectAfterCreate = useMemo(() => {
    if (!catalogueGroupUrl) {
      return props.redirect
    }
    const fallbackGid = parseUtilityTariffGroupIdFromApiUrl(catalogueGroupUrl)
    return (_basePath, _id, data) => {
      const newGid = parseUtilityTariffGroupIdFromApiUrl(data?.utility_tariff_group)
      const gid = newGid != null ? newGid : fallbackGid
      if (gid != null && data?.id != null) {
        return utilityTariffVersionEditPath(gid, data.id)
      }
      return UTILITY_TARIFF_GROUPS_LIST_PATH
    }
  }, [catalogueGroupUrl, props.redirect])

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <Create {...props} redirect={redirectAfterCreate}>
        <SimpleForm
          defaultValue={(a1) => defaultValueFunc(a1, null, defaultCountry, catalogueGroupUrl)}
          hideCancelButton={!!props.hideCancelButton}
          toolbar={props.toolbar}
          formatSubmitValues={(values) => {
            const next = { ...values }
            if (catalogueGroupUrl != null) {
              delete next.utility_tariff_group
            }
            return formatSubmitValues(next, user.email)
          }}
          disabled={!allowCreate}
        >
          {catalogueGroupUrl ? (
            <UtilityTariffCreateBootstrap
              defaultCountry={defaultCountry}
              catalogueGroupUrl={catalogueGroupUrl}
              defaultValueFunc={defaultValueFunc}
            />
          ) : null}
          <TariffInputs isEdit={false} />
        </SimpleForm>
      </Create>
    </ComponentVersionsInherit>
  )
}

export const defaultValueFunc = function (incoming, customisedTariffData, defaultCountry, catalogueGroupUrl) {
  const record = incoming == null ? {} : { ...incoming }
  if (customisedTariffData) {
    record.data = customisedTariffData
  }

  if (!record.country && defaultCountry?.url) {
    record.country = defaultCountry?.url
    record.country_iso2 = defaultCountry?.iso2
  }

  if (!record?.data || record?.data?.length === 0) {
    record.data = JSON.stringify(DEFAULT_TARIFF_DATA)
  }

  var tariffData = typeof record.data === 'string' ? JSON.parse(record.data) : record.data
  //parse schedules
  tariffData = parseSchedules(tariffData)

  if (!tariffData) {
    tariffData = clone(DEFAULT_TARIFF_DATA)
  }

  if (tariffData.wholesale) {
    tariffData.wholesale_marginal_price_limits = tariffData.wholesale_marginal_price_limits || {}
  }

  //get tariff fields from tariffData for customised tariff
  const data = {
    name: record?.name ? record?.name : tariffData?.name,
    code: record?.code ? record?.code : tariffData?.code,
    valid_from: record?.valid_from,
    valid_to: record?.valid_to,
    sector: record?.sector ? record?.sector : tariffData?.applicability?.sector,
    country: record?.country,
    distributor: record?.distributor,
    utility: record?.utility,
    utility_name: record?.utility_name || tariffData?.utility_name,
    is_latest: record?.is_latest,
    is_public_read_only: catalogueGroupUrl != null ? false : record?.is_public_read_only,
    popularity: record?.popularity ? record?.popularity : tariffData?.applicability?.popularity,
    country_iso2: record?.country_iso2 || defaultCountry?.iso2,
    transition_to_utility_tariff: record?.transition_to_utility_tariff,
    test_cases: record?.test_cases,
    has_minimum_charge: record?.has_minimum_charge ? record?.has_minimum_charge : tariffData?.has_minimum_charge,
    has_minimum_export_credit: record?.has_minimum_export_credit
      ? record?.has_minimum_export_credit
      : tariffData?.has_minimum_export_credit,
  }

  if (tariffData?.solar_compensation_settings?.apply_nem3_export_rates) {
    tariffData.export_credits = []
  }

  data.tariffData = tariffData
  if (catalogueGroupUrl == null && record.utility_tariff_group != null && record.utility_tariff_group !== '') {
    data.utility_tariff_group = record.utility_tariff_group
  }
  return data
}

export const formatSubmitValues = (values, userEmail = '', stringifyData = true) => {
  const tariffData = {
    version: '1.0',
    name: values.name,
    code: values.code,
    aliases: values.aliases,
    bill_frequency: values['tariffData']
      ? values['tariffData'].bill_frequency
      : UTILITY_TARIFF_BILL_FREQUENCY_CHOICES[0].id,
    description: values['tariffData'] ? values['tariffData'].description : '',
    tariff_class: values['tariffData']?.tariff_class ?? '',
    applicability: {
      ...values['tariffData'].applicability,
      sector: !isNaN(values?.sector) ? values.sector : SECTOR_RESIDENTIAL,
      popularity: !isNaN(values?.popularity) ? values.popularity : 10,
    },
    solar_compensation_settings: values?.['tariffData']?.solar_compensation_settings
      ? values['tariffData']?.solar_compensation_settings
      : {},
    tax_settings: values['tariffData'] ? values['tariffData'].tax_settings : 'inclusive',
    tax_percentage: Math.min(Math.max(values['tariffData']?.tax_percentage ?? 0, 0), 100),
    has_minimum_charge: values?.has_minimum_charge,
    has_minimum_export_credit: values?.has_minimum_export_credit,
    minimum_charge: values['tariffData'] ? values['tariffData'].minimum_charge : null,
    criteria: values['tariffData'] ? values['tariffData'].criteria : null,
    price_tiering_period: values['tariffData'] ? values['tariffData'].price_tiering_period : 'per_billing_period',
    energy_charges: values['tariffData'] ? values['tariffData'].energy_charges : [],
    shipping_charges: values['tariffData'] ? values['tariffData'].shipping_charges : [],
    demand_charges: values['tariffData'] ? values['tariffData'].demand_charges : [],
    fixed_charges: values['tariffData'] ? values['tariffData'].fixed_charges : [],
    export_credits: values['tariffData'] ? values['tariffData'].export_credits : [],
    pv_capacity_charges: values['tariffData'] ? values['tariffData'].pv_capacity_charges : [],
    controlled_load_charges: values['tariffData'] ? values['tariffData'].controlled_load_charges : [],
    percentage_adjustments_charges: values['tariffData'] ? values['tariffData'].percentage_adjustments_charges : [],
    percentage_adjustments_discounts: values['tariffData'] ? values['tariffData'].percentage_adjustments_discounts : [],
    modified_date: new Date(),
    modified_by_role_email: userEmail ? userEmail : null,
    modified_is_public_read_only_by_role_email: userEmail ? userEmail : null,
    // ui_message_key: values?.['tariffData']?.ui_message_key ? values?.['tariffData']?.ui_message_key : '',
  }

  if (!values?.has_minimum_charge) {
    tariffData.minimum_charge = null
  }

  if (!values?.has_minimum_export_credit) {
    tariffData.solar_compensation_settings.minimum_annual_export_credit = null
  }

  for (const key in tariffData.solar_compensation_settings) {
    if (
      (key !== 'distributed_generation_rules' && key !== 'name') &
      !ExtraFieldByDistributionRule[tariffData.solar_compensation_settings.distributed_generation_rules]?.includes(key)
    )
      delete tariffData.solar_compensation_settings[key]
  }

  if (tariffData?.solar_compensation_settings?.apply_nem3_export_rates) {
    tariffData.export_charges = []
  }

  if (tariffData?.solar_compensation_settings?.minimum_annual_export_credit?.value_by_year)
    tariffData.solar_compensation_settings.minimum_annual_export_credit.value_by_year = csvFloatsToArray(
      tariffData.solar_compensation_settings.minimum_annual_export_credit.value_by_year
    )

  const isNEM3Utility = isCalifornianNEM3Utility(values.utility_name)
  const solarSettings = tariffData.solar_compensation_settings

  if (values.utility_name) {
    tariffData.utility_name = values.utility_name
  }

  applyWholesaleUtilityTariffSubmitFields(values, tariffData)

  if (!isNEM3Utility) {
    delete solarSettings?.apply_nem3_export_rates
  } else {
    delete solarSettings?.apply_nem_modified_with_defined_export_rates
  }

  // Remove low income field if NEM3 export rates are not applicable
  const hasNEM3SettingEnabled =
    solarSettings?.apply_nem_modified_with_defined_export_rates || solarSettings?.apply_nem3_export_rates

  if (!hasNEM3SettingEnabled) {
    delete tariffData.applicability?.is_low_income
  }

  const dataString = JSON.stringify(tariffData)
  values.data = stringifyData ? dataString : tariffData

  return values
}

const mapStateToPropsEdit = (state) => {
  return {
    is_staff: authSelectors.getIsStaff(state),
    org_id: authSelectors.getOrgId(state),
    orgCountryCode: orgSelectors.getOrgIso2(state),
    email: authSelectors.getCurrentUser(state)?.email,
  }
}

export const isCopyingNotYetSaved = () => {
  //@TODO: Remove nasty hack using window.location to detect querystring
  try {
    return window.location.search.indexOf('utility_tariff_id=') > -1
  } catch (e) {
    //
  }
  return false
}

const CreateEditToolbar = (props) => {
  const cancelPath = props.resource === 'utility_tariffs' && props.basePath ? props.basePath : `/${props.resource}`
  return (
    <Toolbar {...props}>
      {!isCopyingNotYetSaved() &&
        (props.resource === 'custom_tariff_current' || props.resource === 'custom_tariff_proposed') && (
          <DeleteButton
            label={props.translate('Clear Custom Tariff')}
            style={{ color: '#4d4d4d', justifyContent: 'flex-start', margin: 0, padding: 6 }}
            resource={props.resource}
            redirect={'/projects/' + props.record.id}
            undoable={false}
          />
        )}
      {props.resource !== 'custom_tariff_current' && (
        <Link style={{ textDecoration: 'none' }} to={cancelPath}>
          <Button
            startIcon={<AlertError />}
            style={{ margin: 10, position: 'relative' }}
            variant="contained"
            size="large"
            color="default"
          >
            <span>{props.translate('Cancel')}</span>
          </Button>
        </Link>
      )}
      {isCopyingNotYetSaved() && props.resource === 'custom_tariff_current' && (
        <Link style={{ textDecoration: 'none' }} to={'/projects/' + props.record.id}>
          <Button
            startIcon={<AlertError />}
            variant="contained"
            color="default"
            style={{ margin: 10, position: 'relative' }}
          >
            <span>{props.translate('Cancel')}</span>
          </Button>
        </Link>
      )}
      <SaveButton
        redirect={false}
        submitOnEnter={true}
        raised={true}
        disabled={props.record.is_public_read_only === true && isReadOnly(props.record, props.org_id)}
      />
    </Toolbar>
  )
}

const DisableFormContent = ({ record, org_id }) => {
  return isReadOnly(record, org_id) ? (
    <div style={{ position: 'absolute', width: '100%', height: '100%', top: '2%', cursor: 'not-allowed', zIndex: 2 }} />
  ) : null
}

class _UtilityTariffEdit extends React.Component {
  isCustom() {
    return this.props.resource === 'custom_tariff_current' || this.props.resource === 'custom_tariff_proposed'
  }

  render() {
    const { prefetchedInitialValues, ...editProps } = this.props
    const { translate, toolbar } = editProps
    const { allowEdit = true, allowDelete = true } = this.props.accessRights || {}
    const showToolBar = allowEdit || this.props.resource !== 'utility_tariffs'
    const customToolbar = toolbar || (
      <CreateEditToolbar
        org_id={this.props.org_id}
        resource={this.props.resource}
        basePath={this.props.basePath}
        translate={translate}
      />
    )
    return (
      <ComponentVersionsInherit versions={ComponentVersions_3_0}>
        <Edit
          {...editProps}
          hasDelete={(record) => {
            if (!record || this.isCustom()) {
              return false
            } else {
              return allowDelete && !isReadOnly(record, this.props.org_id)
            }
          }}
        >
          <SimpleForm
            initialValues={prefetchedInitialValues != null ? prefetchedInitialValues : undefined}
            defaultValue={prefetchedInitialValues != null ? undefined : defaultValueFunc}
            hideCancelButton={!!this.props.hideCancelButton}
            contentClassName="utility-tariff-form-content"
            formatSubmitValues={(values) => {
              return formatSubmitValues(values, this.props.email)
            }}
            toolbar={showToolBar ? customToolbar : null}
            disabled={!allowEdit}
          >
            {!this.isCustom() && <DisableFormContent org_id={this.props.org_id} />}
            <TariffInputs disabled={!allowEdit} isEdit={true} />
          </SimpleForm>
        </Edit>
      </ComponentVersionsInherit>
    )
  }
}

_UtilityTariffEdit.propTypes = {
  showNotification: PropTypes.func,
  push: PropTypes.func,
  record: PropTypes.object,
  prefetchedInitialValues: PropTypes.object,
}

export const UtilityTariffEdit = compose(
  withTranslate,
  connect(mapStateToPropsEdit, {
    showNotification: showNotificationAction,
    push: pushAction,
  })
)(_UtilityTariffEdit)
